import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { HeartHandshake, ShieldCheck, MapPin, Users, ArrowRight, User, Quote, Star } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CORE_SERVICES = [
  'Individual & Family Therapy (OMHC)',
  'Medication Management',
  'Psychiatric Rehabilitation Program (PRP)',
  'Case Management',
  'Comprehensive Assessment',
  'Telehealth Services'
];

const SERVICE_AREAS = ['Pikesville, MD', 'Baltimore City', 'Baltimore County', 'Maryland (Telehealth statewide)'];

const AboutSdoraPage = () => {
  const [teamPreview, setTeamPreview] = useState([]);
  const [testimonialPreview, setTestimonialPreview] = useState([]);
  const [insuranceProviders, setInsuranceProviders] = useState([]);

  useEffect(() => {
    const fetchStaff = async () => {
      try {
        const response = await axios.get(`${API}/staff`);
        const leaders = response.data.filter((s) => s.category === 'Executive Leadership');
        const preview = (leaders.length > 0 ? leaders : response.data).slice(0, 3);
        setTeamPreview(preview);
      } catch {
        // Error handled silently - team preview simply won't render
      }
    };
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonialPreview(response.data.slice(0, 2));
      } catch {
        // Error handled silently - testimonial preview simply won't render
      }
    };
    const fetchInsurance = async () => {
      try {
        const response = await axios.get(`${API}/insurance`);
        setInsuranceProviders(response.data.slice(0, 8));
      } catch {
        // Error handled silently - insurance badge row simply won't render
      }
    };
    fetchStaff();
    fetchTestimonials();
    fetchInsurance();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="About SDORA Wellbeing"
        description="Learn about SDORA Wellbeing LLC — a CARF-accredited, VA Community Care Network provider delivering outpatient mental health therapy, psychiatric rehabilitation, and case management in Pikesville and Baltimore County, MD."
        path="/about"
      />
      <Navigation />

      {/* Hero */}
      <div className="bg-gradient-to-br from-blue-700 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="about-sdora-title">
            About SDORA Wellbeing
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl">
            Compassionate, evidence-based behavioral health care for individuals and families in Pikesville and Baltimore County, Maryland.
          </p>
        </div>
      </div>

      {/* Mission */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              SDORA Wellbeing LLC provides culturally competent, evidence-based outpatient mental health services designed
              to meet each client where they are. Whether you're seeking individual therapy, psychiatric rehabilitation,
              or ongoing medication management, our licensed clinicians work alongside you to build a personalized plan
              of care rooted in respect, accessibility, and lasting recovery.
            </p>
          </div>
        </div>
      </div>

      {/* What We Offer */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What We Offer</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {CORE_SERVICES.map((service) => (
              <div key={service} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-gray-200" data-testid={`about-service-${service.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <HeartHandshake className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700 text-sm font-medium">{service}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/services/comprehensive-mental-health" className="text-blue-600 font-semibold hover:underline inline-flex items-center gap-1" data-testid="about-view-all-services-link">
              View all services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Insurance Accepted */}
      {insuranceProviders.length > 0 && (
        <div className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 justify-center mb-8">
              <ShieldCheck className="w-6 h-6 text-teal-600" />
              <h2 className="text-3xl font-bold text-gray-900">Insurance We Accept</h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {insuranceProviders.map((provider) => (
                <div
                  key={provider.id}
                  className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-center"
                  data-testid={`about-insurance-badge-${provider.id}`}
                >
                  <div className="text-center">
                    <img
                      src={provider.logo_url}
                      alt={provider.name}
                      className="w-full h-12 object-contain mb-2"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <p className="text-xs font-medium text-gray-700">{provider.name}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
              <Link to="/insurance" className="text-blue-600 font-semibold hover:underline inline-flex items-center gap-1" data-testid="about-view-insurance-link">
                See full insurance & payment info <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/get-started" className="px-5 py-2.5 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors text-sm" data-testid="about-verify-coverage-link">
                Verify My Coverage
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Accreditation */}
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 justify-center mb-8">
            <ShieldCheck className="w-6 h-6 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">Licensure & Accreditation</h2>
          </div>
          <div className="flex justify-center items-center gap-8 flex-wrap">
            <div className="bg-gray-50 rounded-lg p-4">
              <img src="https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/c0a5v1h1_mrd-768x189.png" alt="Maryland Department of Health" className="h-16 w-auto" loading="lazy" />
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <img src="https://customer-assets.emergentagent.com/job_wellness-upgrade-1/artifacts/jwyrvdww_carf_logo-500x500.png" alt="CARF Accredited" className="h-16 w-auto" loading="lazy" />
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <img src="https://customer-assets.emergentagent.com/job_01501ca6-ddde-43b6-bfc5-0444b2f0bae0/artifacts/vj4vchgs_proudlycaringforveterans-horizbadge-300x100.png" alt="Proudly Caring for Veterans" className="h-16 w-auto" loading="lazy" />
            </div>
          </div>
          <p className="text-center text-gray-600 max-w-2xl mx-auto mt-8">
            SDORA Wellbeing LLC is CARF accredited and an approved VA Community Care Network (VACCN) provider,
            licensed by the Maryland Department of Health to deliver outpatient mental health and psychiatric
            rehabilitation services.
          </p>
        </div>
      </div>

      {/* Service Area */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 justify-center mb-6">
            <MapPin className="w-6 h-6 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">Where We Serve</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-2xl mx-auto">
            {SERVICE_AREAS.map((area) => (
              <span key={area} className="px-4 py-2 bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700" data-testid={`about-area-${area.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                {area}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center gap-3 justify-center mb-4">
            <Users className="w-6 h-6 text-blue-600" />
            <h2 className="text-2xl font-bold text-gray-900">Get to Know Our Team</h2>
          </div>
          <p className="text-gray-600 mb-8">
            Meet the licensed clinicians behind SDORA Wellbeing, or read what our clients have to say about their experience.
          </p>

          {teamPreview.length > 0 && (
            <div className="grid sm:grid-cols-3 gap-6 mb-10 text-left">
              {teamPreview.map((member) => (
                <div key={member.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`about-team-preview-${member.id}`}>
                  <div className="aspect-square overflow-hidden bg-gray-100">
                    {member.image_url ? (
                      <img src={member.image_url} alt={member.name} className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
                        <User className="w-16 h-16 text-blue-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900">{member.name}</h3>
                    <p className="text-sm text-blue-600 font-medium">{member.title}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {testimonialPreview.length > 0 && (
            <div className="grid sm:grid-cols-2 gap-6 mb-10 text-left max-w-3xl mx-auto">
              {testimonialPreview.map((testimonial) => (
                <div key={testimonial.id} className="bg-blue-50 border border-blue-100 rounded-xl p-6" data-testid={`about-testimonial-preview-${testimonial.id}`}>
                  <Quote className="w-6 h-6 text-blue-300 mb-2" />
                  {testimonial.rating && (
                    <div className="flex gap-0.5 mb-2">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  )}
                  <p className="text-sm text-gray-700 italic mb-3">"{testimonial.text}"</p>
                  <p className="text-sm font-semibold text-gray-900">{testimonial.client_name}</p>
                  {testimonial.location && <p className="text-xs text-gray-500">{testimonial.location}</p>}
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/team" className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors" data-testid="about-meet-team-link">
              Meet the Full Team
            </Link>
            <Link to="/testimonials" className="px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors" data-testid="about-testimonials-link">
              Read Testimonials
            </Link>
            <Link to="/get-started" className="px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors" data-testid="about-get-started-link">
              Start Here
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutSdoraPage;
